import i18n from '@/lib/i18n';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import toast from 'react-hot-toast';

import type { AuthState, LoginForm, UserRole, RolePermissions, UserWithPermissions } from '@/types';
import { tokenStorage } from '@/lib/api';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import { forgetRestaurant, rememberRestaurant } from '@/lib/rememberedRestaurant';

// Comprehensive role-based permission system
const rolePermissions: Record<UserRole, RolePermissions> = {
  super_admin: {
    role: 'super_admin',
    permissions: [
      'view_dashboard',
      'platform_management',
      'multi_tenant_access',
      'customer_impersonation',
      'billing_oversight',
      'platform_analytics',
      'feature_flag_management',
      'system_administration',
      'customer_support',
      'audit_management',
      'subscription_management',
      'revenue_tracking',
      'infrastructure_monitoring',
      'security_oversight',
      'platform_configuration',
      'tenant_provisioning',
      'super_admin_dashboard'
    ],
    canPlaceOrders: false,
    canViewReports: true,
    canManageStaff: true,
    canEditMenu: false,
    canProcessPayments: false,
    canAccessKitchen: false,
    restrictions: ['platform_level_only']
  },
  restaurant_owner: {
    role: 'restaurant_owner',
    permissions: [
      'view_dashboard',
      'manage_staff',
      'view_analytics',
      'manage_settings',
      'view_reports',
      'manage_menu',
      'view_menu',
      'view_kitchen',
      'manage_tables',
      'view_orders',
      'manage_orders',
      'manage_inventory',
      'process_refunds',
      'view_staff_performance',
      'view_audit_logs',
      'process_approvals',
      'emergency_overrides',
      'fraud_prevention'
    ],
    canPlaceOrders: false, // Admin CANNOT place orders as per requirement
    canViewReports: true,
    canManageStaff: true,
    canEditMenu: true,
    canProcessPayments: true,
    canAccessKitchen: true,
    restrictions: ['cannot_place_orders']
  },
  manager: {
    role: 'manager',
    permissions: [
      'view_dashboard',
      'manage_tables',
      'manage_orders',
      'place_orders',
      'view_analytics',
      'manage_menu',
      'view_menu',
      'view_kitchen',
      'manage_staff',
      'process_payments',
      'view_reports'
    ],
    canPlaceOrders: true,
    canViewReports: true,
    canManageStaff: true,
    canEditMenu: true,
    canProcessPayments: true,
    canAccessKitchen: true
  },
  chef: {
    role: 'chef',
    permissions: [
      'view_dashboard',
      'view_kitchen',
      'manage_kitchen_orders',
      'view_menu',
      'update_order_status',
      'view_inventory'
    ],
    canPlaceOrders: false,
    canViewReports: false,
    canManageStaff: false,
    canEditMenu: false,
    canProcessPayments: false,
    canAccessKitchen: true
  },
  waiter: {
    role: 'waiter',
    permissions: [
      'view_dashboard',
      'manage_tables',
      'place_orders',
      'view_orders',
      'update_order_status',
      'view_menu',
      'take_payment'
    ],
    canPlaceOrders: true,
    canViewReports: false,
    canManageStaff: false,
    canEditMenu: false,
    canProcessPayments: true,
    canAccessKitchen: false,
    restrictions: ['assigned_tables_only']
  },
  developer: {
    role: 'developer',
    permissions: [
      'view_dashboard',
      'manage_staff',
      'view_analytics',
      'manage_settings',
      'view_reports',
      'manage_menu',
      'view_menu',
      'view_kitchen',
      'manage_tables',
      'view_orders',
      'manage_orders',
      'place_orders',
      'manage_inventory',
      'process_refunds',
      'view_staff_performance',
      'view_audit_logs',
      'process_approvals',
      'emergency_overrides',
      'fraud_prevention',
      'developer_tools',
      'feature_flags',
      'role_simulation',
      'test_scenarios',
      'debug_mode',
      'system_health',
      'audit_logs',
      'mock_data_generation',
      'environment_controls'
    ],
    canPlaceOrders: true,
    canViewReports: true,
    canManageStaff: true,
    canEditMenu: true,
    canProcessPayments: true,
    canAccessKitchen: true,
    restrictions: ['production_read_only']
  }
};

// Removed mock users - using real backend only
// All users now come from MongoDB via the API

// Enhanced user type with backend compatibility (kept for documentation)
// interface ApiUser { ... }

// Convert API user to internal user format
const mapApiUserToInternal = (apiUser: any): UserWithPermissions => {
  // Defensive programming - handle missing or malformed API response
  if (!apiUser) {
    throw new Error('API user data is null or undefined');
  }

  if (!apiUser.login) {
    throw new Error('API user data is missing required login field');
  }

  // Handle missing role field with proper fallback
  const userRole = apiUser.role || 'waiter';
  const permissions = (rolePermissions as Record<string, RolePermissions>)[userRole] || rolePermissions.waiter;
  
  // Handle missing name fields
  const firstName = apiUser.firstName || 'User';
  const lastName = apiUser.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  
  return {
    id: apiUser.id || apiUser._id || 'unknown',
    login: apiUser.login,
    // The short half — what this person types to sign in, and the only half
    // anyone shows them.
    loginName: apiUser.loginName ?? String(apiUser.login).split('@')[0],
    phone: apiUser.phone,
    name: fullName,
    role: userRole as UserRole,
    avatar: undefined,
    restaurantId: apiUser.restaurantId || undefined,
    isActive: apiUser.status === 'active' || apiUser.isActive === true,
    createdAt: apiUser.createdAt ? new Date(apiUser.createdAt) : new Date(),
    updatedAt: apiUser.updatedAt ? new Date(apiUser.updatedAt) : new Date(),
    permissions,
    shiftStart: new Date(),
    shiftEnd: new Date(Date.now() + 8 * 60 * 60 * 1000),
    assignedTables: userRole === 'waiter' ? ['table_1', 'table_2'] : undefined,
  };
};

// Professional Auth Store Interface with enhanced permissions
interface AuthStore extends AuthState {
  // State
  user: UserWithPermissions | null;
  accessToken: string | null;
  refreshToken: string | null;
  permissions: RolePermissions | null;
  
  // Actions
  login: (credentials: LoginForm) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearAuth: () => void;
  updateUser: (updates: Partial<UserWithPermissions>) => void;
  setUser: (user: UserWithPermissions) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  refreshUserProfile: () => Promise<void>;
  
  // Permission utilities
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole) => boolean;
  canAccess: (resource: string, action: string) => boolean;
  canPlaceOrders: () => boolean;
  getAssignedTables: () => string[];
}

// Professional Auth Store Implementation
export const useAuthStore = create<AuthStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      permissions: null,
      isLoading: true,
      isAuthenticated: false,

      // Login action using real API
      login: async (credentials: LoginForm) => {
        try {
          console.log('🔐 Starting login process...');
          set(draft => {
            draft.isLoading = true;
          });

          // Import AuthService dynamically to avoid circular dependency
          const { AuthService } = await import('@/lib/api');
          const response = await AuthService.login(credentials);
          
          console.log('🔍 Login API response:', response);

          // Map API user to internal format
          const user = mapApiUserToInternal(response.user);
          console.log('✅ Mapped user for login:', user);

          set(draft => {
            draft.user = user;
            draft.accessToken = response.accessToken;
            draft.refreshToken = response.refreshToken;
            draft.permissions = user.permissions;
            draft.isAuthenticated = true;
            draft.isLoading = false;
          });
          
          console.log('✅ Auth state updated successfully');

          /*
           * Remember which restaurant this device signs into.
           *
           * Next time, the form shows the restaurant's name and asks only for
           * the short name — which is the only half a waiter has ever been
           * told. Skipped for the platform operator: `@nova` is not a
           * workplace, and pinning a shared device to it would ask real staff
           * for a name inside a restaurant they do not work in.
           */
          if (response.restaurant?.code && !response.restaurant.isPlatform) {
            rememberRestaurant({
              code: response.restaurant.code,
              name: response.restaurant.name,
            });
          }

          // Clear restaurant data for Super Admin
          if (user.role === 'super_admin') {
            localStorage.removeItem('nova-restaurant');
            forgetRestaurant();
            // Also clear from restaurant store state
            import('@/stores/restaurantStore').then(({ useRestaurantStore }) => {
              const { clearRestaurantData } = useRestaurantStore.getState();
              clearRestaurantData();
            });
          }

          // Store tokens
          tokenStorage.setTokens(response.accessToken, response.refreshToken);

          // Connect to Socket.IO
          connectSocket(user.id, (user as any).restaurantId);

          toast.success(i18n.t('system.success.welcomeBack', { name: user.name }), {
            duration: 3000,
            icon: '👋',
          });
        } catch (error) {
          set(draft => {
            draft.isLoading = false;
            draft.isAuthenticated = false;
            draft.permissions = null;
          });

          const message = error instanceof Error ? error.message : 'Login failed';
          toast.error(message, {
            duration: 4000,
            icon: '❌',
          });
          
          throw error;
        }
      },

      // Logout action using real API
      logout: async () => {
        try {
          // Set flag to prevent auto-login immediately after manual logout
          localStorage.setItem('nova-recent-logout', Date.now().toString());
          
          set(draft => {
            draft.isLoading = true;
          });

          // Import AuthService dynamically
          const { AuthService } = await import('@/lib/api');
          await AuthService.logout();

          // Disconnect socket
          disconnectSocket();

          set(draft => {
            draft.user = null;
            draft.accessToken = null;
            draft.refreshToken = null;
            draft.permissions = null;
            draft.isAuthenticated = false;
            draft.isLoading = false;
          });

          // Clear stored tokens
          tokenStorage.clearTokens();

          toast.success(i18n.t('auth.toast.loggedOutShort'), {
            duration: 2000,
            icon: '👋',
          });
        } catch (error) {
          // Even if logout API fails, clear local state
          // Set flag to prevent auto-login immediately after manual logout
          localStorage.setItem('nova-recent-logout', Date.now().toString());
          
          disconnectSocket();
          
          set(draft => {
            draft.user = null;
            draft.accessToken = null;
            draft.refreshToken = null;
            draft.permissions = null;
            draft.isAuthenticated = false;
            draft.isLoading = false;
          });

          tokenStorage.clearTokens();
          
          toast.success(i18n.t('auth.toast.loggedOutShort'), {
            duration: 2000,
            icon: '👋',
          });
        }
      },

      // Refresh tokens action
      refreshTokens: async () => {
        try {
          const currentRefreshToken = get().refreshToken;
          if (!currentRefreshToken) {
            throw new Error('No refresh token available');
          }

          // Import AuthService dynamically
          const { AuthService } = await import('@/lib/api');
          const response = await AuthService.refreshToken(currentRefreshToken);

          set(draft => {
            draft.accessToken = response.accessToken;
            draft.refreshToken = response.refreshToken;
          });

          // Update stored tokens
          tokenStorage.setTokens(response.accessToken, response.refreshToken);
        } catch (error) {
          // If refresh fails, clear auth state
          get().clearAuth();
          throw error;
        }
      },

      // Check authentication on app load
      checkAuth: async () => {
        try {
          set(draft => {
            draft.isLoading = true;
          });

          const storedAccessToken = tokenStorage.getAccessToken();
          const storedRefreshToken = tokenStorage.getRefreshToken();
          
          if (!storedAccessToken || !tokenStorage.hasValidToken()) {
            console.log('No valid token found, clearing auth state');
            set(draft => {
              draft.user = null;
              draft.accessToken = null;
              draft.refreshToken = null;
              draft.permissions = null;
              draft.isAuthenticated = false;
              draft.isLoading = false;
            });
            tokenStorage.clearTokens();
            return;
          }

          // If we already have user data and valid token, check if we need fresh data
          const currentState = get();
          if (currentState.user && currentState.isAuthenticated && tokenStorage.hasValidToken()) {
            // Check if user data looks complete
            const hasValidName = currentState.user.name && 
                               currentState.user.name !== 'Super Administrator' && 
                               currentState.user.name.length >= 3 &&
                               !currentState.user.name.includes('@');
            
            if (hasValidName) {
              console.log('User already authenticated with valid data, skipping API call');
              set(draft => {
                draft.isLoading = false;
              });

              // Reconnect the socket on this path too. Skipping the profile
              // fetch is fine, but this early return also skipped connecting —
              // so realtime only ever worked in the tab where the user had
              // typed their password. Any reload or direct navigation left the
              // app with no socket at all, which is the normal case for a
              // kitchen display that stays open across a shift.
              connectSocket(currentState.user.id, (currentState.user as any).restaurantId);
              return;
            } else {
              console.log('User authenticated but data looks stale/incomplete, fetching fresh data...');
            }
          }

          console.log('Verifying token with server...');
          
          // Verify token by fetching user profile
          const { UserService } = await import('@/lib/api');
          const apiUser = await UserService.getProfile();
          
          // Map API user to internal format
          const user = mapApiUserToInternal(apiUser);

          console.log('Token verified, user authenticated:', user.login, user.role);

          set(draft => {
            draft.user = user;
            draft.accessToken = storedAccessToken;
            draft.refreshToken = storedRefreshToken;
            draft.permissions = user.permissions;
            draft.isAuthenticated = true;
            draft.isLoading = false;
          });

          // Connect to Socket.IO
          connectSocket(user.id, (user as any).restaurantId);
        } catch (error) {
          console.error('Auth check failed:', error);
          
          // For super admin and critical auth flows, be more resilient to network errors
          const currentUser = get().user;
          const isNetworkError = error instanceof Error && (
            error.message.includes('Network Error') ||
            error.message.includes('NETWORK_ERROR') ||
            error.message.includes('fetch') ||
            error.message.includes('ERR_NETWORK') ||
            error.message.includes('ERR_CONNECTION_REFUSED')
          );
          
          if (isNetworkError && tokenStorage.hasValidToken()) {
            // Network error but token is still valid - keep user logged in
            console.log('Network error during auth check, but token is valid - keeping user logged in');
            
            // If we have stored user data, use it
            if (currentUser) {
              set(draft => {
                draft.isAuthenticated = true;
                draft.isLoading = false;
              });
            } else {
              // No user data but valid token - set loading false but keep token
              set(draft => {
                draft.isLoading = false;
                draft.accessToken = tokenStorage.getAccessToken();
                draft.refreshToken = tokenStorage.getRefreshToken();
              });
            }
          } else {
            // Auth error or token expired - clear auth state
            console.log('Auth error or expired token - clearing auth state');
            set(draft => {
              draft.user = null;
              draft.accessToken = null;
              draft.refreshToken = null;
              draft.permissions = null;
              draft.isAuthenticated = false;
              draft.isLoading = false;
            });

            tokenStorage.clearTokens();
          }
        }
      },

      // Clear authentication state
      clearAuth: () => {
        disconnectSocket();
        
        set(draft => {
          draft.user = null;
          draft.accessToken = null;
          draft.refreshToken = null;
          draft.permissions = null;
          draft.isAuthenticated = false;
          draft.isLoading = false;
        });

        tokenStorage.clearTokens();
      },

      // Update user information
      updateUser: (updates: Partial<UserWithPermissions>) => {
        set(draft => {
          if (draft.user) {
            Object.assign(draft.user, updates);
          }
        });
      },

      // Set user (used by hooks)
      setUser: (user: UserWithPermissions) => {
        set(draft => {
          draft.user = user;
          draft.permissions = user.permissions;
        });
      },

      // Set tokens (used by hooks)
      setTokens: (accessToken: string, refreshToken: string) => {
        set(draft => {
          draft.accessToken = accessToken;
          draft.refreshToken = refreshToken;
        });
      },
      
      // Permission checking utilities
      hasPermission: (permission: string) => {
        const state = get();
        return state.permissions?.permissions?.includes(permission) || false;
      },
      
      hasRole: (role: UserRole) => {
        const state = get();
        return state.user?.role === role;
      },
      
      canAccess: (resource: string, action: string) => {
        const state = get();
        const permissionKey = `${action}_${resource}`;
        return state.permissions?.permissions.includes(permissionKey) || false;
      },
      
      canPlaceOrders: () => {
        const state = get();
        return state.permissions?.canPlaceOrders || false;
      },
      
      getAssignedTables: () => {
        const state = get();
        return state.user?.assignedTables || [];
      },

      // Refresh user profile from server
      refreshUserProfile: async () => {
        try {
          if (!tokenStorage.hasValidToken()) {
            console.log('No valid token, cannot refresh user profile');
            return;
          }

          console.log('Refreshing user profile from server...');
          const { UserService } = await import('@/lib/api');
          const apiUser = await UserService.getProfile();
          
          // Map API user to internal format
          const user = mapApiUserToInternal(apiUser);
          
          set(draft => {
            draft.user = user;
            draft.permissions = user.permissions;
          });
          
          console.log('User profile refreshed:', user.name, user.login);
        } catch (error) {
          console.error('Failed to refresh user profile:', error);
          throw error;
        }
      },

      // Development helper to refresh permissions
      refreshPermissions: () => {
        const state = get();
        if (state.user) {
          const updatedPermissions = rolePermissions[state.user.role];
          set(draft => {
            if (draft.user) {
              draft.user.permissions = updatedPermissions;
              draft.permissions = updatedPermissions;
            }
          });
        }
      },
    })),
    {
      name: 'nova-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        console.log('🔄 Rehydrating auth store...');
        console.log('📋 Rehydrated state:', {
          hasUser: !!state?.user,
          userLogin: state?.user?.login,
          userName: state?.user?.name,
          userRole: state?.user?.role,
          isAuthenticated: state?.isAuthenticated,
          hasToken: !!state?.accessToken
        });
        
        // Fix rehydration issues for all users
        if (state?.user) {
          console.log('🔧 Fixing user data during rehydration...');
          
          // Fix permissions if they're malformed (empty array instead of proper object)
          if (!state.permissions || Array.isArray(state.permissions) || !state.permissions.permissions) {
            const correctPermissions = rolePermissions[state.user.role];
            if (correctPermissions) {
              console.log('⚠️ Fixing malformed permissions for', state.user.role);
              state.permissions = correctPermissions;
              state.user.permissions = correctPermissions;
            }
          }
          
          // If name is missing or looks like a fallback, force fresh data fetch
          if (!state.user.name || 
              state.user.name === 'Super Administrator' || 
              state.user.name.length < 3) {
            console.log('⚠️ Name is missing or looks like fallback, will fetch fresh data');
            // Don't set fallback name - let checkAuth handle it
          }
          
          console.log('✅ Fixed user data:', {
            name: state.user.name,
            role: state.user.role,
            login: state.user.login
          });
        }
        
        // More robust auth check after rehydration
        const hasStoredToken = tokenStorage.getAccessToken();
        const hasValidToken = tokenStorage.hasValidToken();
        
        console.log('Rehydration check:', {
          hasStoredToken: !!hasStoredToken,
          hasValidToken,
          hasUser: !!state?.user,
          userRole: state?.user?.role
        });
        
        if (hasStoredToken && hasValidToken && state) {
          // We have a valid token, proceed with gentle auth check
          console.log('Valid token found, checking auth with server...');
          
          // Set initial state as authenticated if we have user data
          if (state.user) {
            state.isAuthenticated = true;
            state.isLoading = false;
          }
          
          // Do a gentle auth check that won't immediately clear auth on network errors
          setTimeout(() => {
            state.checkAuth().catch((error) => {
              console.log('Auth check failed on rehydration:', error.message);
              // Don't clear auth here - let the user try to use the app
              // The checkAuth method will handle network vs auth errors appropriately
            });
          }, 100); // Small delay to let the app initialize
          
        } else {
          // No valid token, clear auth state
          console.log('No valid token found, clearing auth state');
          if (state) {
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.permissions = null;
            state.isAuthenticated = false;
            state.isLoading = false;
          }
          tokenStorage.clearTokens();
        }
      },
    }
  )
);

// Enhanced auth helpers with comprehensive permission system
export const authHelpers = {
  // Check if user has specific role
  hasRole: (user: UserWithPermissions | null, role: UserRole): boolean => {
    return user?.role === role;
  },

  // Check if user has any of the specified roles
  hasAnyRole: (user: UserWithPermissions | null, roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  },

  // Check if user has specific permission
  hasPermission: (user: UserWithPermissions | null, permission: string): boolean => {
    if (!user || !user.permissions || !user.permissions.permissions) return false;
    return user.permissions.permissions.includes(permission);
  },

  // Check if user can perform action on resource
  canPerform: (user: UserWithPermissions | null, action: string, resource?: string): boolean => {
    if (!user || !user.permissions || !user.permissions.permissions) return false;
    
    const permissionKey = resource ? `${action}_${resource}` : action;
    return user.permissions.permissions.includes(permissionKey);
  },

  // Check if user can place orders (specific business rule)
  canPlaceOrders: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.canPlaceOrders || false;
  },

  // Check if user can access kitchen
  canAccessKitchen: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.canAccessKitchen || false;
  },

  // Check if user can view reports
  canViewReports: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.canViewReports || false;
  },

  // Check if user can manage staff
  canManageStaff: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.canManageStaff || false;
  },

  // Check if user can edit menu
  canEditMenu: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.canEditMenu || false;
  },

  // Check if user can process payments
  canProcessPayments: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.canProcessPayments || false;
  },

  // Get assigned tables for waiters
  getAssignedTables: (user: UserWithPermissions | null): string[] => {
    return user?.assignedTables || [];
  },

  // Check if user is restricted to assigned tables only
  isRestrictedToAssignedTables: (user: UserWithPermissions | null): boolean => {
    return user?.permissions?.restrictions?.includes('assigned_tables_only') || false;
  },

  // Get user display name
  getDisplayName: (user: UserWithPermissions | null): string => {
    if (!user) return 'Guest';
    return user.name || user.loginName || user.login;
  },

  // Get user initials
  getInitials: (user: UserWithPermissions | null): string => {
    if (!user) return 'G';
    
    const name = user.name || user.loginName || user.login;
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  },

  /**
   * The one place a role becomes a label.
   *
   * `roles.*` is keyed by the enum value itself, so there is no lookup table
   * to fall out of date — a role that exists has a label, and adding a role
   * without one shows up immediately.
   *
   * There used to be a second implementation in employeeStore reading a
   * separate `employees.roles.*` namespace, and the two disagreed: the same
   * person was "Менеджер" in the sidebar and "Менеджер ресторана" on the staff
   * list, "Повар" one place and "Шеф-повар" another. employeeStore now
   * delegates here.
   *
   * The fallback humanises the enum rather than printing it raw — a missing
   * label should read as an oversight, not as `super_admin` leaking into the UI.
   */
  getRoleDisplayName: (role: UserRole): string => {
    const label = i18n.t(`roles.${role}`, { defaultValue: '' });
    if (label) return label;
    return String(role)
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  // Get role color for UI
  getRoleColor: (role: UserRole): string => {
    // Roles are distinguished by their label and by VALUE, not by hue — the
    // design system is near black-and-white. Only super_admin gets the filled
    // treatment, because it is the one role worth flagging on sight.
    const elevated = 'text-white bg-ink-700 border border-ink-700';
    const standard = 'text-neutral-700 bg-white border border-neutral-300';

    const roleColors: Record<UserRole, string> = {
      super_admin: elevated,
      restaurant_owner: standard,
      manager: standard,
      chef: standard,
      waiter: standard,
      developer: standard
    };
    return roleColors[role] || standard;
  }
};

// Export role permissions for development (mockUsers removed)
export { rolePermissions };

// Development mode auto-login function for demo purposes (DISABLED for Super Admin)
export const initDevelopmentMode = () => {
  // Auto-login disabled - Super Admin should login manually
  console.log('🚀 Development mode: Auto-login disabled - please login manually');
  
  // Add global debug functions for development
  if (import.meta.env.DEV) {
    (window as any).refreshUserProfile = () => {
      const { refreshUserProfile } = useAuthStore.getState();
      return refreshUserProfile();
    };
    
    (window as any).checkAuth = () => {
      const { checkAuth } = useAuthStore.getState();
      return checkAuth();
    };
    
    console.log('🔧 Debug functions available: refreshUserProfile(), checkAuth()');
  }
};